/**
 * Start formats as a horizontal rail of poster cards (the streaming-app shelf): each card is a wide piece of artwork —
 * the format's own colour field, its name and promise in large type, and the sample itself standing in a phone frame —
 * still at rest, playing (muted, looping) on hover or keyboard focus, drawn by the same scene library as the real
 * video. The rail scrolls sideways, so more formats never cost more height.
 */
import { useMemo, useRef, useState } from 'react';
import { Player, Thumbnail } from '@remotion/player';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { VideoComposition } from '@/video/VideoComposition';
import { ASPECT_SIZE, VIDEO_FPS, totalFrames } from '@/video/spec';
import { STILL_AT, starterPreview } from './starterPreviews';
import type { VideoStarter } from './videoStudioShared';

/** Each format's colour field: two stops from the Beatly palette (purple, lime, teal, coral), so the shelf reads as a set. */
const ART: Record<string, { from: string; to: string; ink: string }> = {
  'creator-ad': { from: '#2e024c', to: '#7a1fbf', ink: '#ffffff' },
  'brand-motion': { from: '#a9f600', to: '#4f8a00', ink: '#101828' },
  'campaign-recap': { from: '#00b1a5', to: '#03524d', ink: '#ffffff' },
  testimonial: { from: '#be43ff', to: '#46236a', ink: '#ffffff' },
  launch: { from: '#ff5635', to: '#8a1d07', ink: '#ffffff' },
  reasons: { from: '#101828', to: '#344054', ink: '#ffffff' },
  'before-after': { from: '#46236a', to: '#00b1a5', ink: '#ffffff' },
  event: { from: '#7a1fbf', to: '#ff5635', ink: '#ffffff' },
};
const FALLBACK = { from: '#2e024c', to: '#46236a', ink: '#ffffff' };

export function StarterCard({ starter, selected, onPick }: { starter: VideoStarter; selected: boolean; onPick: () => void }) {
  const [live, setLive] = useState(false);
  const spec = useMemo(() => starterPreview(starter.id), [starter.id]);
  const input = useMemo(() => ({ spec }), [spec]);
  const size = ASPECT_SIZE[spec.aspect];
  const frames = Math.max(1, totalFrames(spec));
  const still = Math.min(frames - 1, Math.round((STILL_AT[starter.id] ?? 0.6) * VIDEO_FPS));
  const art = ART[starter.id] || FALLBACK;
  const box = 'pointer-events-none h-full w-full';
  return (
    <button type="button" onClick={onPick} aria-pressed={selected}
      onMouseEnter={() => setLive(true)} onMouseLeave={() => setLive(false)} onFocus={() => setLive(true)} onBlur={() => setLive(false)}
      style={{ background: `linear-gradient(135deg, ${art.from} 0%, ${art.to} 100%)`, color: art.ink }}
      className={`group relative flex h-[196px] w-[316px] shrink-0 snap-start overflow-hidden rounded-[20px] text-left shadow-[0_6px_16px_-4px_rgba(16,24,40,0.18)] outline-none transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-[0_14px_28px_-8px_rgba(16,24,40,0.32)] focus-visible:-translate-y-1 focus-visible:scale-[1.02] ${selected ? 'ring-[3px] ring-[#be43ff] ring-offset-2' : ''}`}>
      {/* The format's icon, huge and faint: texture for the artwork. */}
      <starter.icon aria-hidden className="pointer-events-none absolute -bottom-6 -left-5 h-32 w-32 opacity-[0.09]" strokeWidth={1.25} />
      <span className="relative z-10 flex min-w-0 flex-1 flex-col justify-between p-5">
        <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] opacity-75"><starter.icon className="h-3.5 w-3.5" />{starter.seconds} s format</span>
        <span className="flex flex-col gap-1.5">
          <span className="text-[21px] font-bold leading-[1.1] tracking-[-0.01em]">{starter.name}</span>
          <span className="line-clamp-3 text-[12.5px] leading-snug opacity-80">{starter.line}</span>
        </span>
      </span>
      {/* The sample standing in a phone frame, tilted until hovered. */}
      <span className="relative z-10 my-4 mr-4 h-[164px] w-[92px] shrink-0 rotate-[4deg] overflow-hidden rounded-[14px] border-[3px] border-white/90 bg-black shadow-[0_10px_24px_rgba(0,0,0,0.35)] transition-transform duration-300 ease-out group-hover:rotate-0 group-hover:scale-105">
        {live ? (
          <Player component={VideoComposition} inputProps={input} durationInFrames={frames} compositionWidth={size.width} compositionHeight={size.height} fps={VIDEO_FPS}
            autoPlay loop initiallyMuted numberOfSharedAudioTags={0} controls={false} clickToPlay={false} className={box} style={{ width: '100%', height: '100%' }} />
        ) : (
          <Thumbnail component={VideoComposition} inputProps={input} durationInFrames={frames} compositionWidth={size.width} compositionHeight={size.height} fps={VIDEO_FPS} frameToDisplay={still} className={box} style={{ width: '100%', height: '100%' }} />
        )}
      </span>
    </button>
  );
}

/** The shelf: one row that scrolls sideways (wheel, trackpad, swipe or the arrows), cards snapping into place. */
export function StarterRail({ starters, selectedId, onPick }: { starters: VideoStarter[]; selectedId?: string; onPick: (s: VideoStarter) => void }) {
  const rail = useRef<HTMLDivElement>(null);
  const scroll = (dir: 1 | -1) => rail.current?.scrollBy({ left: dir * 660, behavior: 'smooth' });
  const arrow = 'grid h-8 w-8 place-items-center rounded-full border border-[#eaecf0] bg-white text-[#344054] shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:bg-[#f9fafb]';
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-[15px] font-semibold text-[#101828]">Start from a format</span>
          <span className="text-[12.5px] text-[#667085]">Hover a card to watch it move.</span>
        </span>
        <span className="hidden gap-1.5 sm:flex">
          <button type="button" aria-label="Previous formats" onClick={() => scroll(-1)} className={arrow}><ChevronLeft className="h-4 w-4" /></button>
          <button type="button" aria-label="More formats" onClick={() => scroll(1)} className={arrow}><ChevronRight className="h-4 w-4" /></button>
        </span>
      </div>
      {/* A scroller clips whatever spills past it: the padding leaves room for the shadows and the hover lift, the
          negative margins give that room back to the layout, so no shadow is ever cut into a hard edge. */}
      <div ref={rail} className="-mx-6 -mb-8 -mt-2 flex snap-x snap-mandatory scroll-px-6 gap-4 overflow-x-auto scroll-smooth px-6 pb-10 pt-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {starters.map((st) => <StarterCard key={st.id} starter={st} selected={selectedId === st.id} onPick={() => onPick(st)} />)}
      </div>
    </div>
  );
}
